import { useState } from "react";
import { toast } from "sonner";
import { Phone, MessageCircle, MapPin, Clock, Mail, ArrowRight } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BRAND } from "@/config/brand";
import { TESTIDS } from "@/constants/testIds";

const SERVICES = [
  "Siding",
  "Roofing",
  "Ceramic Floors",
  "Kitchen & Baths",
  "Cement Work",
  "Coping",
  "Power Washing",
  "Doormats",
  "Other",
];

export default function ContactForm() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: "Siding",
    address: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.service) {
      toast.error("Please fill in name, phone and service.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      if (!payload.address) delete payload.address;
      if (!payload.message) delete payload.message;
      const res = await api.createLead(payload);
      toast.success(res.message || "Thanks — we'll be in touch shortly.");
      setForm({
        name: "",
        phone: "",
        email: "",
        service: "Siding",
        address: "",
        message: "",
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit. Please try calling us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative bg-[color:var(--vs-bg)] py-24 md:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-14">
          {/* Left: form */}
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-[color:var(--vs-blue)]" />
              <span className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[color:var(--vs-blue)]">
                005 / Free Quote
              </span>
            </div>
            <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl font-bold leading-[0.95] tracking-tight">
              Let&apos;s build
              <br />
              <span className="chrome-text">something</span>{" "}
              <span className="text-[color:var(--vs-blue)]">solid.</span>
            </h2>
            <p className="mt-6 text-gray-400 max-w-lg">
              Tell us about your project. We reply the same business day with
              next steps and — if you&apos;re ready — schedule your free estimate.
            </p>

            <form
              onSubmit={submit}
              data-testid={TESTIDS.contact.form}
              className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              <Field label="Full name *">
                <input
                  type="text"
                  required
                  data-testid={TESTIDS.contact.name}
                  value={form.name}
                  onChange={change("name")}
                  className="vs-input"
                  placeholder="John Smith"
                />
              </Field>
              <Field label="Phone *">
                <input
                  type="tel"
                  required
                  data-testid={TESTIDS.contact.phone}
                  value={form.phone}
                  onChange={change("phone")}
                  className="vs-input"
                  placeholder="516-000-0000"
                />
              </Field>
              <Field label="Email">
                <input
                  type="email"
                  data-testid={TESTIDS.contact.email}
                  value={form.email}
                  onChange={change("email")}
                  className="vs-input"
                  placeholder="you@example.com"
                />
              </Field>
              <Field label="Service *">
                <select
                  required
                  data-testid={TESTIDS.contact.service}
                  value={form.service}
                  onChange={change("service")}
                  className="vs-input appearance-none"
                >
                  {SERVICES.map((s) => (
                    <option key={s} value={s} className="bg-black text-white">
                      {s}
                    </option>
                  ))}
                </select>
              </Field>
              <div className="md:col-span-2">
                <Field label="Property address">
                  <input
                    type="text"
                    data-testid={TESTIDS.contact.address}
                    value={form.address}
                    onChange={change("address")}
                    className="vs-input"
                    placeholder="123 Main St, Hicksville, NY"
                  />
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Project details">
                  <textarea
                    rows={5}
                    data-testid={TESTIDS.contact.message}
                    value={form.message}
                    onChange={change("message")}
                    className="vs-input resize-none"
                    placeholder="Tell us what you're planning…"
                  />
                </Field>
              </div>
              <div className="md:col-span-2 pt-3">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid={TESTIDS.contact.submit}
                  className="btn-vs btn-primary w-full md:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? "Sending…" : "Request Free Quote"}
                  {!loading && <ArrowRight className="w-4 h-4" />}
                </button>
              </div>
            </form>
          </div>

          {/* Right: contact info */}
          <div className="lg:col-span-5 lg:col-start-9">
            <div className="border border-white/10 bg-[#0f0f10] p-8 md:p-10 flex flex-col gap-8 sticky top-28">
              <InfoRow
                icon={Phone}
                label="Call"
                value={BRAND.phone}
                href={`tel:${BRAND.phoneRaw}`}
              />
              <InfoRow
                icon={MessageCircle}
                label="WhatsApp"
                value={BRAND.whatsapp}
                href={`https://wa.me/1${BRAND.whatsappRaw}`}
              />
              <InfoRow icon={Mail} label="Email" value={BRAND.email} href={`mailto:${BRAND.email}`} />
              <InfoRow icon={MapPin} label="Service area" value={BRAND.areas.join(" · ")} />
              <InfoRow icon={Clock} label="Hours" value={BRAND.hours} />

              <div className="pt-2 border-t border-white/10">
                <div className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white/40 mb-3">
                  Emergency roofing?
                </div>
                <a
                  href={`tel:${BRAND.phoneRaw}`}
                  className="btn-vs btn-primary w-full justify-center"
                >
                  <Phone className="w-4 h-4" />
                  Call now
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .vs-input {
          width: 100%;
          background: #050505;
          border: 1px solid #27272a;
          color: #fff;
          padding: 14px 16px;
          font-family: 'DM Sans', sans-serif;
          font-size: 15px;
          outline: none;
          transition: border-color .15s, box-shadow .15s;
        }
        .vs-input:focus {
          border-color: #0055ff;
          box-shadow: 0 0 0 3px rgba(0,85,255,0.15);
        }
        .vs-input::placeholder { color: #6b7280; }
      `}</style>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white/50 mb-2 block">
        {label}
      </span>
      {children}
    </label>
  );
}

function InfoRow({ icon: Icon, label, value, href }) {
  const inner = (
    <div className="flex items-start gap-4 group">
      <div className="w-10 h-10 shrink-0 flex items-center justify-center border-2 border-[color:var(--vs-blue)]/60 group-hover:bg-[color:var(--vs-blue)] transition-colors">
        <Icon className="w-4 h-4 text-[color:var(--vs-blue)] group-hover:text-white transition-colors" />
      </div>
      <div>
        <div className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white/40 mb-1">
          {label}
        </div>
        <div className="text-white font-display uppercase tracking-tight text-lg leading-tight">
          {value}
        </div>
      </div>
    </div>
  );
  return href ? (
    <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer">
      {inner}
    </a>
  ) : (
    inner
  );
}
